import { AlgoliaResponse } from '@hooks/useAlgoliaQuery';

export type AlgoliaIndexName =
  | 'products-{0}'
  | 'products-{0}_newest'
  | 'products-{0}_rating_desc'
  | 'products-{0}_rating_count_desc'
  | 'products-{0}_query_suggestions'
  | 'documents-{0}';

export const AlgoliaIndexes = {
  // Products
  products: 'products-{0}' as AlgoliaIndexName,
  products_newest: 'products-{0}_newest' as AlgoliaIndexName,
  products_rating_desc: 'products-{0}_rating_desc' as AlgoliaIndexName,
  products_rating_count_desc: 'products-{0}_rating_count_desc' as AlgoliaIndexName,
  products_query_suggestions: 'products-{0}_query_suggestions' as AlgoliaIndexName,

  // Documents
  documents: 'documents-{0}' as AlgoliaIndexName,
};

export type AlgoliaIndex = keyof typeof AlgoliaIndexes;

export const ALGOLIA_APP_ID = 'T9PUD27XJ6';
export const ALGOLIA_SEARCH_API_KEY = '34c221e876b680a0f246a599a0a0692e';

interface ProductHitCategoryListItem {
  name: string;
  url: string;
}

interface ProductPressRelease {
  title: string;
  url: string;
}

export type AlgoliaItemType = 'Product' | 'Document' | 'PressRelease';

export enum AlgoliaItemTypePriority {
  Document = 10,
  PressRelease = 20,
  Product = 30,
}

export interface AlgoliaRelatedItem {
  itemType: AlgoliaItemType;
  guid: string;
  title: string | null;
  url: string | null;
  createdWhen: string;
}

export interface AlgoliaBaseHit<T = unknown> {
  objectID: string;
  guid: string;
  itemType: AlgoliaItemType;
  itemTypePriority: AlgoliaItemTypePriority;
  cultureCode: string;
  createdWhen: string;
  title: string | null;
  url: string | null;
  image: string | null;
  categories: Record<string, string[]>;
  pageViews: number;
  isPublished: boolean;
  data: T;
  relatedItems: AlgoliaRelatedItem[];

  // Algolia fields
  query?: string;
  _highlightResult?: {
    title?: {
      value: string;
    };
    query?: {
      fullyHighlighted: boolean;
      matchLevel: string;
      matchedWords: string[];
      value: string;
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    categoryList?: any[];
  };
}

export type AlgoliaProductHit = AlgoliaBaseHit<{
  agilityId: number;
  articleNumbers: string[];
  rating: number;
  numberOfRatings: number;
  isNew: boolean;
  modelCode: string;
  variantCodes: string[];
  categoryList: ProductHitCategoryListItem[];
  hierarchicalCategories: Record<string, string>;
  tagText: string;
  agilityIdMapping?: Record<string, number>;
  pressReleases?: ProductPressRelease[] | null;
}>;

const disabledFacets = ['isPublished'];

export function applyAlgoliaFacetOrdering(facets: string[], response: AlgoliaResponse<unknown>): string[] {
  // Filter out disabled facets
  facets = facets.filter((facet) => !disabledFacets.includes(facet));

  const facetOrderList = response.renderingContent?.facetOrdering?.facets?.order ?? [];
  const defaultOrderList = Object.keys(response.facets ?? {});

  if (facetOrderList.length > 0) {
    facets = facets.filter((facet) => facetOrderList.includes(facet));
  }

  return facets.slice().sort((a, b) => {
    // order by order in facetOrderList or by the number of hits
    const aIndex = facetOrderList.indexOf(a);
    const bIndex = facetOrderList.indexOf(b);

    if (aIndex === -1 && bIndex === -1) {
      // If both elements are not found in indexList, use default order
      return defaultOrderList.indexOf(a) - defaultOrderList.indexOf(b);
    } else if (aIndex === -1) {
      // If only element A is not found in indexList, move it to the end
      return 1;
    } else if (bIndex === -1) {
      // If only element B is not found in indexList, move it to the end
      return -1;
    }

    // Compare indices if both elements are found in indexList
    return aIndex - bIndex;
  });
}

export function applyAlgoliaFacetItemOrdering(
  facetName: string,
  facet: Record<string, number>,
  response: AlgoliaResponse<unknown>,
): Record<string, number> {
  const lookup = response.renderingContent?.facetOrdering?.values?.[facetName];

  if (!lookup) {
    return facet;
  }

  const defaultFacetKeys = Object.keys(facet);
  const orderedFacetKeys: string[] = [];

  // Add in the pinned items
  for (const facetKey of defaultFacetKeys) {
    if (lookup.order?.includes(facetKey)) {
      orderedFacetKeys.push(facetKey);
    }
  }

  // Then add in the rest of the items
  const remaningFacetKeys = defaultFacetKeys.filter((key) => !orderedFacetKeys.includes(key));
  switch (lookup.sortRemainingBy) {
    case 'alpha': {
      orderedFacetKeys.push(...remaningFacetKeys.sort());
      break;
    }

    default:
    case 'count': {
      orderedFacetKeys.push(...remaningFacetKeys.sort((a, b) => facet[b] - facet[a]));
      break;
    }

    case 'hidden': {
      break;
    }
  }

  // Construct a new object with the ordered keys
  const orderedFacet: Record<string, number> = {};

  for (const key of orderedFacetKeys) {
    orderedFacet[key] = facet[key];
  }

  // return it
  return orderedFacet;
}

export function getAlgoliaIndex(index: AlgoliaIndexName, cultureCode: string, env: string | null): AlgoliaIndex {
  if (env) {
    return index.replace('{0}', `${env}-${cultureCode.toLowerCase()}`) as AlgoliaIndex;
  }

  return index.replace('{0}', cultureCode.toLowerCase()) as AlgoliaIndex;
}

export function getProductWithVariantUrl(hit: AlgoliaProductHit, searchTerm: string): string {
  let url = hit.url ?? '';

  if (hit.data.agilityIdMapping) {
    const agilityId = hit.data.agilityIdMapping[searchTerm];

    if (agilityId && agilityId > 0) {
      url = `${hit.url}?variant=${agilityId}`;
    }
  }

  return url;
}
